import {ezprOrange} from '../_helpers';
import {EZPRTheme} from '../_theme';
import React, {useCallback, useRef, useState} from 'react';
import PropTypes from 'prop-types';

import {
    Alert,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    IconButton,
    MenuItem,
    Portal,
    Select,
    Snackbar,
    TextField,
    ThemeProvider,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import LoadingButton from '@mui/lab/LoadingButton';
import {ErrorPopup} from '../pr_editor/dialogs/ErrorPopup.jsx';
import DeleteIcon from '@mui/icons-material/DeleteOutlined';
import {BrickContext, File, Transaction} from 'olympe';
import Tooltip from '@mui/material/Tooltip';
import {
    encryptPasswordLambda,
    getJsonFromExcelLambda,
    sendEmailLambda,
    uploadRecordsLambda
} from '../../utils/functionSignatures';
import UserController from '../../controllers/UserController';
import AdminChangeLog from '../../models/adminApp/AdminChangeLog';
import {format} from 'date-fns';
import {ADMIN_APP_TABLE_COLUMNS, ADMIN_APP_TABLE_NAMES, LAMBDA_NAMES} from '../../utils/constants';
import {getMailContentAdminAppChange} from '../../utils/email';

function removeDuplicates(rows, keys) {
    const seen = new Set();
    return rows.filter((r) => {
        const key = keys.map((k) => r[k]).join('|');
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
    });
}


/**
 * This is the upload Records popup that it's used for the mass upload in the admin app
 * @param {UploadRecordsPopup.propTypes} props
 * @return {JSX.Element}
 * @constructor
 */
export default function UploadRecordsPopup(props) {
    const ACTION_TYPE = {
        CREATE: 'upsert',
        DELETE: 'delete',
    };

    const inputRef = useRef();

    const [errorMsg, setErrorMsg] = useState([]);
    const [appEventCode, setAppEventCode] = useState('');
    const [isValidForm, setIsValidForm] = useState(false);
    const [sheetName, setSheetName] = useState('');
    const [actionType, setActionType] = useState(ACTION_TYPE.CREATE);
    const [uploadRecordsLoading, setUploadRecordsLoading] = useState(false);
    const fileName = useRef('');
    const [uploadedFile, setUploadedFile] = useState('');
    const [snackbar, setSnackbar] = useState(null);

    const handleCloseSnackbar = () => setSnackbar(null);

    const handleProcessSuccess = useCallback((message) => {
        setSnackbar({children: message, severity: 'success'});
    }, []);

    const checkIfFormIsValid = () => {
        setIsValidForm(fileName.current !== '' && actionType !== '');
    };
    const handleCloseErrorPopup = () => {
        setErrorMsg([]);
        setAppEventCode('');
    };

    const parseUploadDBError = (error) => {
        // High-level mapping of frequent DB/SQL/Knex patterns to user-friendly messages.
        const getUserFriendlyMessage = (rawError) => {
            if (!rawError || typeof rawError !== 'string') {
                return null;
            }

            const lower = rawError.toLowerCase();

            // Duplicate / unique constraint violations (including existing custom message).
            if (rawError.includes('Duplicate row detected') ||
                lower.includes('duplicate entry') ||
                lower.includes('violation of primary key constraint') ||
                lower.includes('unique constraint')) {
                return 'Some rows in the file create duplicates compared to existing data. Please remove or correct the duplicate rows and try again.';
            }

            // Invalid type conversions: varchar -> int / numeric / date.
            if ((rawError.includes('Conversion failed') && rawError.includes('to data type int')) ||
                lower.includes('error converting data type varchar to numeric') ||
                (rawError.includes('Conversion failed') && rawError.includes('date and/or time')) ||
                lower.includes('failed to convert parameter value')) {
                return 'Some columns that must contain only numbers or dates contain text or invalid formats. Please ensure numeric columns contain only digits and date columns use the expected date format.';
            }

            // Invalid column name / unknown column (including __EMPTY).
            if (lower.includes('invalid column name') || lower.includes('unknown column')) {
                if (rawError.includes('__EMPTY')) {
                    return 'Your file contains one or more columns without a header name. Please give these columns a proper name or remove them, then try again.';
                }
                return 'Your file contains column names that do not match the expected configuration. Please review the column headers in your file and align them with the list shown on the screen.';
            }

            // NOT NULL / required column missing.
            if (lower.includes('cannot insert the value null') ||
                lower.includes('does not allow nulls') ||
                lower.includes('cannot be null') ||
                lower.includes('null value in column')) {
                return 'Some required columns are empty in the file (for example an identifier, company code, or other mandatory field). Please fill in all required columns for each row and try again.';
            }

            // Foreign key constraint violations.
            if (lower.includes('foreign key constraint') ||
                lower.includes('conflicted with the foreign key constraint')) {
                return 'Some values in the file refer to records that do not exist in the system (for example a company code, supplier, or user that is unknown). Please verify these codes and correct them in the file.';
            }

            // Numeric out of range / overflow.
            if (lower.includes('arithmetic overflow') ||
                lower.includes('out of range value') ||
                lower.includes('numeric overflow')) {
                return 'Some numeric values are too large or too small to be accepted (for example an amount or identifier outside the allowed range). Please adjust these values in the file and try again.';
            }

            // String length / truncation issues.
            if (lower.includes('string or binary data would be truncated') ||
                lower.includes('data too long for column')) {
                return 'Some text values are too long for the corresponding fields. Please shorten these values (for example names or descriptions) and try again.';
            }

            // CHECK constraint violations (invalid allowed values).
            if (lower.includes('check constraint') ||
                lower.includes('violates check constraint')) {
                return 'Some values are not allowed for one or more fields (for example an invalid status or code). Please make sure the values in the file match the allowed values.';
            }

            // Locking / timeout / deadlock issues.
            if (lower.includes('lock wait timeout exceeded') ||
                lower.includes('deadlock') ||
                lower.includes('timeout expired')) {
                return 'The system is temporarily busy processing other operations. Please try again in a few moments. If the problem persists, contact support.';
            }

            // Generic SQL syntax / internal query issues: keep technical details away from the user.
            if (lower.includes('incorrect syntax near') ||
                lower.includes('syntax error') ||
                lower.includes('sqlstate')) {
                return 'A technical error occurred while processing your file. Please try again later or contact support if the issue continues.';
            }

            return null;
        };

        try {
            // If it is an error from Knex, the format is often JSON stringified.
            const parsedError = JSON.parse(error);
            const parsedErrorMsgFromDB = JSON.parse(parsedError.messageFromDB);
            const rawError = parsedErrorMsgFromDB && parsedErrorMsgFromDB.error ? parsedErrorMsgFromDB.error : error;

            // Try to extract just the DB error part if it follows "Error: <SQL query> - <Error message>".
            let dbErrorMessage = rawError;
            if (typeof rawError === 'string' && rawError.includes(' - ')) {
                const parts = rawError.split(' - ');
                dbErrorMessage = parts[parts.length - 1];
            }

            const friendly = getUserFriendlyMessage(dbErrorMessage);
            if (friendly) {
                return [friendly, `Here's the raw error from system: ${dbErrorMessage}`];
            }

            // Fallback if no pattern matched but we still have a parsed error string.
            return [`Failed to update ${props.selectedTable}: ${dbErrorMessage}`];
        } catch (e) {
            // Error is not JSON, or parsing failed: work with the raw string.
            console.log(e);

            const friendly = getUserFriendlyMessage(error);
            if (friendly) {
                return [friendly, `Raw error from system: ${error}`];
            }

            // Last resort: show a generic message without exposing low-level DB details.
            return [`Failed to update ${props.selectedTable} due to a technical error. Please review your file and try again. If the problem persists, contact support.`];
        }
    };

    const handleDBOperation = async (operationData) => {
        uploadRecordsLambda(
            props.context,
            props.context.get(LAMBDA_NAMES.OLYMPE_DB_MASS_UPSERT),
            props.selectedTableValue,
            operationData,
            actionType,
        )
            .then(async (results) => {
                const changes = [];
                operationData.forEach((newRow) => {
                    Object.keys(newRow).forEach((key) => {
                        changes.push({column: key, old: '<File Upload>', new: newRow[key], idx: newRow.idx, companyCode: newRow.company_code});
                    });
                });

                const currentUser = UserController.getCurrentUserName('EZPR-USER');
                const currentUserName = localStorage.getItem('userFullName');
                const adminChangesMap = {};

                const t = new Transaction();
                changes.forEach((change) => {
                    const newChange = t.create(AdminChangeLog);
                    t.update(newChange, AdminChangeLog.dateProp, new Date());
                    t.update(newChange, AdminChangeLog.userNameProp, currentUserName);
                    t.update(newChange, AdminChangeLog.userIDProp, currentUser);
                    t.update(newChange, AdminChangeLog.tableNameProp, props.selectedTableValue);
                    t.update(newChange, AdminChangeLog.columnProp, change.column);
                    t.update(newChange, AdminChangeLog.oldValueProp, change.old);
                    t.update(newChange, AdminChangeLog.newValueProp, change.new);
                    change.idx && t.update(newChange, AdminChangeLog.idxProp, change.idx);
                    change.companyCode && t.update(newChange, AdminChangeLog.companyCodeProp, change.companyCode);

                    if (!adminChangesMap[change.companyCode]) {
                        adminChangesMap[change.companyCode] = {changes: [], admins: []};
                    }
                    adminChangesMap[change.companyCode].changes.push({
                        idx: change.idx,
                        date: format(new Date(), 'dd.MM.yyyy HH:mm'),
                        userName: currentUserName,
                        userID: currentUser,
                        table: ADMIN_APP_TABLE_NAMES[props.selectedTableValue],
                        column: ADMIN_APP_TABLE_COLUMNS[change.column],
                        oldValue: change.old,
                        newValue: change.new,
                    });
                });
                await t.executeAsLarge();

                props.adminUserList.forEach((admin) => {
                    if (admin.company_code && admin.company_code !== '') {
                        for (const companyCode in adminChangesMap) {
                            if (adminChangesMap.hasOwnProperty(companyCode) && admin.company_code.includes(companyCode)) {
                                adminChangesMap[companyCode].admins.push(admin.email);
                            }
                        }
                    }
                });

                for (const companyCode in adminChangesMap) {
                    if (adminChangesMap.hasOwnProperty(companyCode)) {
                        const {admins, changes} = adminChangesMap[companyCode];
                        const mailContent = getMailContentAdminAppChange(companyCode, changes);
                        const adminEmails = admins.join(';');
                        if (adminEmails) {
                            await sendEmailLambda(props.context, props.sendEmail, adminEmails, '', '', `[Easy Request] Configuration table change`, mailContent, []);
                        }
                    }
                }

                const actionMsg = actionType === ACTION_TYPE.CREATE ? 'created / updated' : 'deleted';
                resetInput();
                checkIfFormIsValid();
                handleProcessSuccess(`The records are successfully ${actionMsg}.`);

                setUploadRecordsLoading(false);
            })
            .catch((e) => {
                setUploadRecordsLoading(false);

                try {
                    const parsedError = JSON.parse(e.message);
                    setAppEventCode(parsedError.appEventCode);
                    setErrorMsg(parseUploadDBError(e.message));
                } catch (_) {
                    setErrorMsg(parseUploadDBError(e.message));
                }
            });
    };

    const extractConflictColumns = (currentTable) => {
        const CONSTRAINS = {
            sb_group: 'grp_name,email',
            strategic_buyer_commodity: 'commodity,company_code,total_cost,cus_psl_vendor_status',
            strategic_buyer_supplier: 'supplier_id,company_code,total_cost,cus_psl_vendor_status',
            admin_app_user_list_mapping: 'name,email,user_id',
            catalogs: 'company_code,vendor_code',
        };
        return CONSTRAINS.hasOwnProperty(currentTable) ? CONSTRAINS[currentTable].split(',') : [];
    };

    const handleUpload = async () => {
        setUploadRecordsLoading(true);
        const transaction = new Transaction();

        // Read file
        const reader = new FileReader();
        reader.onloadend = () => {
            const tag = File.createFromContent(
                transaction,
                uploadedFile.name,
                reader['result'],
                uploadedFile.type,
            );
            transaction.persist(tag, false);

            // Execute transaction
            transaction
                .execute()
                .then(() => {
                    getJsonFromExcelLambda(
                        props.context,
                        props.context.get('Get Json From Excel'),
                        tag,
                        sheetName,
                        async (result) => {
                            if (result.length > 0) {
                                const rows = Array.isArray(result[0]) ? result[0] : result;
                                const validKeys = extractConflictColumns(props.selectedTableValue);
                                const dedupedRows = removeDuplicates(rows, validKeys);
                                if (!dedupedRows || dedupedRows.length === 0) {
                                    setUploadRecordsLoading(false);
                                    setErrorMsg(['The file contains no valid rows to upload.']);
                                    return;
                                }
                                const firstElement = dedupedRows[0];
                                const missingColumns = validKeys.filter((key) => !Object.keys(firstElement).includes(key));

                                if (Object.hasOwn(dedupedRows[0], 'password')) {
                                    const encrypt = props.context.get(LAMBDA_NAMES.ENCRYPT);
                                    for (const row of dedupedRows) {
                                        row.password = (await encryptPasswordLambda(props.context, encrypt, row.password))[0];
                                    }
                                }

                                if (missingColumns.length === 0) {
                                    await handleDBOperation(dedupedRows);
                                } else {
                                    setUploadRecordsLoading(false);
                                    setErrorMsg(
                                        [`The provided File doesn't contain the correct column names. Please check that the following columns (${missingColumns.join(', ')}) are present and try again `],
                                    );
                                }
                            } else {
                                setUploadRecordsLoading(false);
                                setErrorMsg(['The provided File is empty.']);
                            }
                        },
                        (e) => {
                            setUploadRecordsLoading(false);
                            setErrorMsg([`Failed to parse file ${uploadedFile.name} : ${e}`]);
                        },
                    );
                })
                .catch((message) => {
                    setUploadRecordsLoading(false);
                    setErrorMsg(
                        [`Failed to create file ${uploadedFile.name} in DC: ${message}.`],
                    );
                });
        };

        reader.readAsArrayBuffer(uploadedFile);
    };
    const handleSheetName = (e) => {
        setSheetName(e.target.value);
        checkIfFormIsValid();
    };
    const handleActionTypeChange = (e) => {
        setActionType(e.target.value);
        checkIfFormIsValid();
    };
    const handleUploadFile = (event) => {
        if (event.target.files.length > 0) {
            const file = event.target.files[0];
            const extension = file.name.split('.').pop().toLowerCase();
            const allowed = ['xls', 'xlsx'];

            if (allowed.indexOf(extension) === -1) {
                alert(`Please upload a file that has the xls or xlsx extension.`);
                resetInput();
                checkIfFormIsValid();
                return;
            }
            const selectableMaxFileSize = 1024 * 1024 * 1;
            const fileSize = file.size;

            // Do not allow users to upload files higher size than 1 MB
            if (fileSize > selectableMaxFileSize) {
                alert(`Please upload a file smaller than 1 MB.`);
                resetInput();
                checkIfFormIsValid();
                return;
            }
            fileName.current = file.name;
            setUploadedFile(file);
            checkIfFormIsValid();
        } else {
            fileName.current = '';
            setUploadedFile(null);
            checkIfFormIsValid();
        }
    };
    const resetInput = () => {
        fileName.current = '';
        setUploadRecordsLoading(false);
        setUploadedFile(null);
    };
    return (
        <ThemeProvider theme={EZPRTheme}>
            <Dialog open={true} onClose={props.handleOnClose} fullWidth={true}>
                <div className={'pr-popup-header'}>
                    <DialogTitle>Upload Records - {props.selectedTable}</DialogTitle>
                    <IconButton
                        aria-label="close"
                        onClick={props.handleOnClose}
                        sx={{
                            color: ezprOrange,
                        }}
                    >
                        <CloseIcon
                            sx={{
                                width: '50px',
                                height: '50px',
                            }}
                        />
                    </IconButton>
                </div>
                <DialogContent>
                    <div className={'popup-upload-records-content'}>
                        <div>Upload File: *</div>
                        {fileName.current === '' && (
                            <Button
                                variant="outlined"
                                component="label"
                                sx={{
                                    'color': '#fff',
                                    'backgroundColor': ezprOrange,
                                    '&:hover': {backgroundColor: '#F2ae49'},
                                    ':disabled': {backgroundColor: 'rgba(0,0,0,0.26)'},
                                }}
                            >
                Browse
                                <input
                                    // Hidden file input
                                    type="file"
                                    multiple={false}
                                    accept="*"
                                    hidden
                                    ref={inputRef}
                                    // Events
                                    onChange={handleUploadFile}
                                />
                            </Button>
                        )}
                        {fileName.current !== '' && (
                            <div style={{display: 'flex', alignItems: 'center'}}>
                                <span style={{flexGrow: '4'}}>{fileName.current}</span>
                                <IconButton
                                    component="span"
                                    size={'small'}
                                    onClick={resetInput}
                                >
                                    <DeleteIcon />
                                </IconButton>
                            </div>
                        )}
                        <div>Action: *</div>
                        <FormControl fullWidth>
                            <Select
                                size={'small'}
                                value={actionType}
                                onChange={handleActionTypeChange}
                            >
                                <MenuItem value={ACTION_TYPE.CREATE}>Create/Update</MenuItem>
                                <MenuItem value={ACTION_TYPE.DELETE}>Delete</MenuItem>
                            </Select>
                        </FormControl>
                        <div>Sheet Name: </div>
                        <TextField
                            variant={'standard'}
                            size={'small'}
                            fullWidth
                            value={sheetName}
                            onChange={handleSheetName}
                            placeholder="Specific Sheet Name from the file"
                        />
                    </div>
                    <span>
            *Note: The file that you are uploading should have the following
                        column names: </span>
                    <div style={{display: 'flex', flexWrap: 'wrap'}}>{
                        Array.from(props.columnsDisplayName).map((col, idx) => {
                            return (col[0] !== 'idx' && (
                                <>
                                    <Tooltip title={col[1]}>
                                        <div>{`${col[0]}${idx < (props.columnsDisplayName.size - 1) ? ',' : ''}`}&nbsp;</div>
                                    </Tooltip>
                                </>
                            ));
                        })}
                    </div>
                </DialogContent>
                <DialogActions>
                    <LoadingButton
                        sx={{
                            'color': '#fff',
                            'backgroundColor': ezprOrange,
                            '&:hover': {backgroundColor: '#F2ae49'},
                            ':disabled': {backgroundColor: 'rgba(0,0,0,0.26)'},
                        }}
                        variant={'outlined'}
                        onClick={handleUpload}
                        disabled={!isValidForm}
                        loading={uploadRecordsLoading}
                    >
                        <span>Upload</span>
                    </LoadingButton>
                </DialogActions>
            </Dialog>
            {errorMsg.length !== 0 && (
                <ErrorPopup
                    onClose={handleCloseErrorPopup}
                    open={true}
                    title={'Records Upload Failed'}
                    errorMessages={errorMsg}
                    appEventCode={appEventCode}
                />
            )}
            {!!snackbar && (
                <Portal>
                    <Snackbar
                        open
                        anchorOrigin={{vertical: 'top', horizontal: 'center'}}
                        onClose={handleCloseSnackbar}
                        autoHideDuration={6000}
                    >
                        <Alert {...snackbar} onClose={handleCloseSnackbar} />
                    </Snackbar>
                </Portal>
            )}
        </ThemeProvider>
    );
}

UploadRecordsPopup.propTypes = {
    context: PropTypes.instanceOf(BrickContext).isRequired,
    selectedTable: PropTypes.string,
    selectedTableValue: PropTypes.string,
    columns: PropTypes.string,
    handleOnClose: PropTypes.func,
    adminUserList: PropTypes.array,
    sendEmail: PropTypes.object,
};
